import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calculator, Loader2, TrendingUp } from 'lucide-react';
import { authApi } from '@/lib/api/auth';

const FLOAT_SYMBOLS: { ch: string; className: string; style?: React.CSSProperties }[] = [
  { ch: '∑', className: 'top-[6%] right-[8%] text-4xl sm:text-5xl animate-math-float' },
  { ch: '∫', className: 'top-[18%] left-[4%] text-3xl sm:text-4xl animate-math-drift', style: { animationDelay: '0.8s' } },
  { ch: 'π', className: 'top-[32%] right-[14%] text-2xl sm:text-3xl animate-math-float', style: { animationDelay: '1.2s' } },
  { ch: '√', className: 'bottom-[28%] left-[6%] text-3xl animate-math-drift', style: { animationDelay: '0.3s' } },
  { ch: '%', className: 'bottom-[20%] right-[10%] text-3xl sm:text-4xl animate-math-float', style: { animationDelay: '1.5s' } },
  { ch: '∞', className: 'top-[48%] left-[12%] text-2xl animate-math-drift', style: { animationDelay: '2s' } },
  { ch: 'Σ', className: 'bottom-[40%] right-[18%] text-2xl sm:text-3xl animate-math-float', style: { animationDelay: '0.5s' } },
  { ch: 'Δ', className: 'top-[12%] left-[20%] text-xl sm:text-2xl animate-math-drift' },
];

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || '/';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.login({ username, password });
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در ورود.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-100 via-amber-50/90 to-amber-100/80"
      dir="rtl"
    >
      {/* الگوی شبکه ملایم */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgb(120 53 15 / 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(120 53 15 / 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />

      {/* نمادهای ریاضی شناور */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {FLOAT_SYMBOLS.map((s, i) => (
          <span
            key={i}
            className={`absolute select-none font-mono text-amber-700/25 ${s.className}`}
            style={s.style}
            dir="ltr"
          >
            {s.ch}
          </span>
        ))}
        {/* اعداد تزئینی */}
        <span
          className="absolute top-[25%] left-[30%] text-sm sm:text-base font-mono text-amber-800/20 animate-pulse-soft"
          dir="ltr"
        >
          ۱۲٬۴۵۰٬۰۰۰
        </span>
        <span
          className="absolute bottom-[35%] right-[22%] text-xs sm:text-sm font-mono text-amber-800/15 animate-pulse-soft"
          style={{ animationDelay: '1s' }}
          dir="ltr"
        >
          ۱۷٪
        </span>
      </div>

      {/* منحنی نمودار پس‌زمینه */}
      <svg
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-36 sm:h-44 text-amber-600"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          className="login-chart-path"
          d="M0,100 C200,20 400,100 600,50 C800,0 1000,80 1200,30"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          className="login-chart-path login-chart-path--delay"
          d="M0,80 C300,100 500,30 800,60 C950,80 1100,40 1200,70"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.5"
        />
      </svg>

      <div className="relative z-10 w-full max-w-md mx-4 animate-card-rise">
        <div className="relative rounded-2xl border border-amber-200/60 bg-white/90 p-6 sm:p-8 shadow-2xl shadow-amber-900/10 backdrop-blur-sm ring-1 ring-amber-100/80">
          <div
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-50"
            style={{
              background:
                'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, transparent 40%, rgba(120, 53, 15, 0.06) 100%)',
            }}
          />

          <div className="relative text-center mb-8">
            <div className="inline-flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg animate-icon-glow">
              <Calculator className="h-9 w-9" strokeWidth={1.75} />
            </div>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-slate-800">حسابداری</h1>
            <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
              ورود امن به سامانه مالی
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-amber-800/80">
              <TrendingUp size={14} className="shrink-0" />
              <span>گزارش‌ها و دفاتر در یک نگاه</span>
            </div>
          </div>

          {error && (
            <div className="relative mb-4 rounded-xl border border-red-200/80 bg-red-50/90 px-3 py-2.5 text-center text-sm text-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="relative space-y-4">
            <div>
              <label htmlFor="username" className="mb-1 block text-sm font-medium text-slate-700">
                نام کاربری
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200/90 bg-white/80 px-4 py-2.5 text-slate-800 shadow-sm transition focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 disabled:bg-slate-100"
                placeholder="نام کاربری"
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">
                رمز عبور
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="w-full rounded-xl border border-slate-200/90 bg-white/80 px-4 py-2.5 text-slate-800 shadow-sm transition focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 disabled:bg-slate-100"
                placeholder="رمز عبور"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !username || !password}
              className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-amber-600 to-amber-500 py-2.5 font-medium text-white shadow-md transition hover:from-amber-700 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span
                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                aria-hidden
              />
              <span className="relative inline-flex w-full items-center justify-center gap-2">
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    در حال ورود...
                  </>
                ) : (
                  <>
                    <Calculator className="h-5 w-5" />
                    ورود
                  </>
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/login-otp', { state: { from } })}
              className="w-full rounded-xl border border-amber-200 bg-amber-50/60 py-2.5 text-sm font-medium text-amber-800 transition hover:bg-amber-100"
            >
              ورود با شماره موبایل (کد پیامکی)
            </button>
            <p className="text-center text-xs text-slate-500 leading-relaxed">
              همان شماره‌ای که برای کاربر در سامانه ثبت شده (فیلد موبایل) و پیامک برای آن فعال است.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
